import FavoriteProductView from 'chairisher/view/product/favorite';
import ReferrerUtils from 'chairisher/util/referrer';

import { getAmplitudeEventPropertiesForProduct } from 'chairisher/analytics/product';
import { getNumSellerSales } from 'chairisher/context/account';
import { getPrimaryCountry, getShippableCountryByCode } from 'chairisher/context/location';
import {
    getAreMessagesEnabled,
    getNumProductReviews,
    getPdpType,
    getProductRating,
    getProductSellerType,
    isSampleAvailable,
} from 'chairisher/context/product';
import { getNumSellerReviews, getSellerRating } from 'chairisher/context/sellerreviews';
import { AmplitudeEventProperty, logAmplitudeEvent, TrackingScreen } from 'chairisher/util/analytics';

/**
 * Tracks the display of the product detail page
 *
 * @param {ProductModel} product The product being displayed in the PDP
 */
export function trackProductDetailPageview(product, metaProductData) {
    const userCountry =
        getShippableCountryByCode(metaProductData?.shipping_location?.country_code || getPrimaryCountry()).display ||
        '';

    const amplitudeEventProperties = {
        ...getAmplitudeEventPropertiesForProduct(product),
        [AmplitudeEventProperty.ARE_MESSAGES_ENABLED]: getAreMessagesEnabled(),
        [AmplitudeEventProperty.HAS_PRIVATE_DEAL]: product.getHasPrivateDeal(),
        [AmplitudeEventProperty.IS_FAVORITED_BY_VIEWER]: FavoriteProductView.isProductIdFavorited(product.getId()),
        [AmplitudeEventProperty.IS_MULTIPLE]: product.getIsMultiple(),
        [AmplitudeEventProperty.IS_SAMPLE_AVAILABLE]: isSampleAvailable(),
        [AmplitudeEventProperty.NUM_PRODUCT_REVIEWS]: getNumProductReviews(),
        [AmplitudeEventProperty.NUM_SELLER_REVIEWS]: getNumSellerReviews(),
        [AmplitudeEventProperty.NUM_SELLER_SALES]: getNumSellerSales(),
        [AmplitudeEventProperty.PRODUCT_RATING]: getProductRating(),
        [AmplitudeEventProperty.PRODUCT_STATE_DISPLAY]: product.getProductStateDisplay(),
        [AmplitudeEventProperty.PRODUCT_SELLER_TYPE]: getProductSellerType(),
        [AmplitudeEventProperty.SELLER_RATING]: getSellerRating(),
        [AmplitudeEventProperty.IS_IN_OTHER_CARTS]: !!metaProductData?.num_other_carts_in,
        [AmplitudeEventProperty.IS_ON_HOLD]: metaProductData?.is_on_hold,
        [AmplitudeEventProperty.IS_IN_CART]: metaProductData?.is_in_cart,
        [AmplitudeEventProperty.HAS_MADE_OFFER]: metaProductData?.has_made_offer,
        [AmplitudeEventProperty.IS_USER_FOLLOWING_SELLER]: metaProductData?.is_following_seller,
        [AmplitudeEventProperty.TYPE]: getPdpType(),
        [AmplitudeEventProperty.USER_COUNTRY]: userCountry,
        [AmplitudeEventProperty.USER_POSTAL_CODE]: metaProductData?.shipping_location?.postal_code,
    };

    const [referreringCollection] =
        ReferrerUtils.getReferrer().match(/\/(city|collection|maker|room|style)\/[a-zA-Z0-9-]+/) ?? [];
    if (referreringCollection) {
        amplitudeEventProperties[AmplitudeEventProperty.REFERRING_COLLECTION] = referreringCollection;
    }

    logAmplitudeEvent('screen - product detail', amplitudeEventProperties);
}

/**
 * Tracks a product's shipping information
 *
 * @param {ProductModel} product The product that is being viewed
 * @param {number} preAdjustedPrice pre adjustment shipping price
 */
export function trackProductShippingChanged(product, preAdjustedPrice) {
    const amplitudeEventProperties = {
        [AmplitudeEventProperty.SHIPPING_COST]: product.getShippingCost(),
        [AmplitudeEventProperty.SHIPPING_PARTNER_CODE]: product.getShippingPartnerCode(),
        [AmplitudeEventProperty.SCREEN]: TrackingScreen.PDP,
        // shipping engine cost is an estimate so long as we round shipping up to the next whole number ending in 9
        [AmplitudeEventProperty.SHIPPING_ENGINE_COST]: preAdjustedPrice,
    };

    const shippingDistance = product.getShippingDistance();
    if (shippingDistance !== undefined) {
        amplitudeEventProperties[AmplitudeEventProperty.SHIPPING_DISTANCE] = shippingDistance;
    }

    logAmplitudeEvent('product - shipping change', amplitudeEventProperties);
}

/**
 * Tracks the display of the sample modal from the product detail page
 *
 * @param {ProductModel} product The product that is being viewed
 * @param {string} displayType The type of display to track; e.g. 'open' and 'close'
 */
export function trackSampleModalDisplay(product, displayType) {
    const amplitudeEventProperties = getAmplitudeEventPropertiesForProduct(product);
    logAmplitudeEvent(`sample modal - ${displayType}`, amplitudeEventProperties);
}
